/**
 * ShopField — the one searchable shop/location picker, shared anywhere a spend
 * needs a place (cash spend, add-expense visit). Three honest ways to a pin,
 * never just a dropdown: (1) SEARCH your saved places by name, (2) 📍 use my
 * current location, (3) type an ADDRESS or shop and geocode it, then a small
 * live map lets you drag the pin to the exact spot. The shared SearchField
 * (with its VoiceButton) dictates the search. Emits either an existing { placeId } or a fresh { newPlace }
 * descriptor — the host creates the place on submit. Reversible, no silent
 * writes.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../api.js';
import SearchField from './SearchField.jsx';

const JHB = [-26.2041, 28.0473]; // sensible SA default when we have no fix
const PIN = L.divIcon({
  className: '', html: '<div style="font-size:26px;line-height:26px;transform:translate(-3px,-20px)">📍</div>',
  iconSize: [26, 26], iconAnchor: [10, 24],
});

/**
 * @param {object} p
 * @param {{placeId?:number,newPlace?:object,name?:string,address?:string,lat?:number,lng?:number}|null} p.value
 * @param {(v:any)=>void} p.onChange  selection changed (null = no shop)
 * @param {string} [p.expenseTitle]   context that sharpens the AI resolve
 */
export default function ShopField({ value, onChange, expenseTitle = '' }) {
  const [saved, setSaved] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);   // resolve() candidates
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);          // {lat,lng} live fix
  const [open, setOpen] = useState(false);       // picker expanded (search mode)
  const [err, setErr] = useState('');
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const rgTimer = useRef(null);

  // sel: the resolved selection with coords + label, or null.
  const sel = value && (value.lat != null || value.placeId || value.newPlace)
    ? (value.newPlace ? { ...value.newPlace, newPlace: true } : value)
    : null;

  useEffect(() => {
    api('/budget/places/').then((d) => setSaved(Array.isArray(d) ? d : (d?.results || []))).catch(() => {});
    navigator.geolocation?.getCurrentPosition(
      (g) => setPos({ lat: g.coords.latitude, lng: g.coords.longitude }),
      () => {}, { timeout: 10000, maximumAge: 600000, enableHighAccuracy: false });
  }, []);

  // Search saved + geocoder as you type (debounced) — no "Find" button needed.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true); setErr('');
      try {
        const d = await api('/budget/places/resolve/', {
          method: 'POST',
          body: { query: q, ...(expenseTitle ? { title: expenseTitle } : {}), ...(pos || {}) },
        });
        setResults(d.candidates || []);
      } catch (e) { setErr(e.data?.detail || 'Search unavailable — pick a saved place or drop a pin.'); }
      finally { setBusy(false); }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, pos]);

  const savedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return saved
      .filter((sp) => !q || (sp.name || '').toLowerCase().includes(q) || (sp.address || '').toLowerCase().includes(q))
      .slice(0, q ? 40 : 6);
  }, [saved, query]);

  const pick = (v) => { onChange?.(v); setOpen(false); setResults([]); setQuery(''); };
  const pickSaved = (sp) => pick({ placeId: sp.id, name: sp.name, address: sp.address, lat: sp.lat != null ? Number(sp.lat) : null, lng: sp.lng != null ? Number(sp.lng) : null });
  const pickCandidate = (c) => pick({ newPlace: { name: c.name, address: c.address || '', lat: c.lat, lng: c.lng, source: c.source || 'shop' }, name: c.name, address: c.address, lat: c.lat, lng: c.lng });

  const useHere = async () => {
    setErr(''); setBusy(true);
    let pt = pos;
    if (!pt) {
      pt = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (g) => res({ lat: g.coords.latitude, lng: g.coords.longitude }),
          () => res(null), { timeout: 10000, maximumAge: 600000, enableHighAccuracy: false });
      });
      if (pt) setPos(pt);
    }
    if (!pt) { setErr('Could not get your location — allow location access.'); setBusy(false); return; }
    const fallback = { name: 'My current location', address: '', lat: pt.lat, lng: pt.lng, source: 'address' };
    try {
      const d = await api('/budget/places/reverse_geocode/', { method: 'POST', body: { lat: pt.lat, lng: pt.lng } });
      const p = (d && d.lat != null) ? d : fallback;
      pick({ newPlace: { name: p.name || 'My current location', address: p.address || '', lat: p.lat, lng: p.lng, source: 'address' }, name: p.name, address: p.address, lat: p.lat, lng: p.lng });
    } catch { pick({ newPlace: fallback, name: fallback.name, address: '', lat: pt.lat, lng: pt.lng }); }
    setBusy(false);
  };

  // Move the pin → become a custom address at the dropped point, reverse-geocoded.
  const movePin = (lat, lng) => {
    onChange?.({ newPlace: { name: (sel?.name && sel?.name !== 'My current location') ? sel.name : 'Pinned location', address: sel?.address || '', lat, lng, source: 'address' }, name: sel?.name || 'Pinned location', address: sel?.address || '', lat, lng });
    if (rgTimer.current) clearTimeout(rgTimer.current);
    rgTimer.current = setTimeout(async () => {
      try {
        const d = await api('/budget/places/reverse_geocode/', { method: 'POST', body: { lat, lng } });
        if (d && d.lat != null) onChange?.({ newPlace: { name: (sel?.name && sel?.name !== 'My current location') ? sel.name : (d.name || 'Pinned location'), address: d.address || '', lat, lng, source: 'address' }, name: (sel?.name && sel?.name !== 'My current location') ? sel.name : d.name, address: d.address, lat, lng });
      } catch { /* keep the dropped point */ }
    }, 600);
  };

  // Live map: init once when we first have a coordinate to show; keep the pin
  // and centre in sync as the selection changes. Never a white screen — any
  // Leaflet hiccup is swallowed and the picker still works without the map.
  useEffect(() => {
    if (!sel || sel.lat == null || sel.lng == null) return;
    try {
      if (!mapRef.current && mapEl.current) {
        const m = L.map(mapEl.current, { zoomControl: false, attributionControl: false })
          .setView([sel.lat, sel.lng], 15);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(m);
        const mk = L.marker([sel.lat, sel.lng], { icon: PIN, draggable: true }).addTo(m);
        mk.on('dragend', () => { const ll = mk.getLatLng(); movePin(ll.lat, ll.lng); });
        m.on('click', (e) => { mk.setLatLng(e.latlng); movePin(e.latlng.lat, e.latlng.lng); });
        mapRef.current = m; markerRef.current = mk;
        setTimeout(() => { try { m.invalidateSize(); } catch { /* */ } }, 60);
      } else if (mapRef.current && markerRef.current) {
        markerRef.current.setLatLng([sel.lat, sel.lng]);
        mapRef.current.setView([sel.lat, sel.lng]);
        setTimeout(() => { try { mapRef.current.invalidateSize(); } catch { /* */ } }, 40);
      }
    } catch { /* map optional — search + pin coords already captured */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.lat, sel?.lng]);

  useEffect(() => () => { try { mapRef.current?.remove(); } catch { /* */ } mapRef.current = null; }, []);

  // COLLAPSED: show the chosen shop as a chip; tap to change.
  if (sel && !open) {
    return (
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">{sel.newPlace ? '📍' : '🏪'}</span>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{sel.name || 'Pinned location'}</div>
            {sel.address ? <div className="text-[11px] text-slate-500 truncate">{sel.address}</div> : null}
          </div>
          <button type="button" className="ml-auto text-xs text-vblue hover:underline" onClick={() => setOpen(true)}>Change</button>
          <button type="button" className="text-xs text-slate-500 hover:text-slate-300" onClick={() => pick(null)}>Clear</button>
        </div>
        {sel.lat != null && sel.lng != null && (
          <div ref={mapEl} className="h-40 rounded-lg overflow-hidden border border-edge" />
        )}
        {sel.lat != null && sel.lng != null && (
          <input className="input text-sm" placeholder="Name this spot (optional)"
                 value={sel.name === 'Pinned location' || sel.name === 'My current location' ? '' : (sel.name || '')}
                 onChange={(e) => onChange?.({ ...(value || {}), newPlace: { ...(value?.newPlace || { lat: sel.lat, lng: sel.lng, address: sel.address, source: sel.source || 'address' }), name: e.target.value || 'Pinned location' }, name: e.target.value })} />
        )}
      </div>
    );
  }

  // EXPANDED: search + current location + saved + geocoded candidates.
  return (
    <div className="grid gap-2">
      {!sel && !open && (
        <button type="button" className="input text-left text-slate-500 flex items-center gap-2" onClick={() => setOpen(true)}>
          🔎 <span>Search a shop or address…</span>
          <span className="ml-auto text-[11px] text-slate-600">or 📍 here</span>
        </button>
      )}
      {open && (
        <>
          <SearchField value={query} onChange={setQuery} onVoice={setQuery}
                       autoFocus parse={false}
                       placeholder="Search saved, a shop, or an address" />
          <button type="button"
                  className="text-left border border-vblue/50 rounded-lg px-2.5 py-1.5 text-sm text-vblue hover:bg-vblue/10"
                  disabled={busy} onClick={useHere}>
            📍 Use my location{busy ? ' …' : ''}
          </button>
          {err && <p className="text-xs text-amber-400">{err}</p>}
          <div className="grid gap-1.5 max-h-56 overflow-y-auto">
            {savedFiltered.map((sp) => (
              <button key={`sp${sp.id}`} type="button"
                      className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vblue w-full min-w-0 block truncate"
                      onClick={() => pickSaved(sp)}>
                🏪 {sp.name}{sp.address ? <span className="text-[11px] text-slate-500"> · {sp.address}</span> : null}
              </button>
            ))}
            {busy && results.length === 0 && <p className="text-xs text-slate-500 px-1">Searching…</p>}
            {results.map((c, i) => (
              <button key={`sr${i}`} type="button"
                      className="text-left border border-edge rounded-lg px-2.5 py-1.5 text-sm hover:border-vpurple w-full min-w-0 block"
                      onClick={() => pickCandidate(c)}>
                <span className="font-medium block truncate">{c.name}
                  <span className={`ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded ${c.source === 'address' ? 'text-amber-400 border border-amber-400/40' : 'text-vpurple border border-vpurple/40'}`}>
                    {c.source === 'address' ? 'address' : 'shop'}
                  </span>
                </span>
                {c.distance_km != null && <span className="text-[11px] text-vpurple"> · {c.distance_km} km</span>}
                <span className="block text-[11px] text-slate-500 truncate">{c.address}</span>
              </button>
            ))}
          </div>
          <button type="button" className="text-[11px] text-slate-600 text-left" onClick={() => { setOpen(false); if (!sel) pick(null); }}>
            {sel ? 'cancel' : 'No place — just cash'}
          </button>
        </>
      )}
    </div>
  );
}
